import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  LayoutDashboard, Activity, Server, Landmark, Database, Settings,
  Hotel, RefreshCw, Loader2, Eye, Shield, Zap, Globe, Brain,
  ChevronRight, Users, DollarSign, Film, HardDrive, CheckCircle2,
  Gavel, BarChart3, Menu, X, Package
} from 'lucide-react';
import { toast } from 'sonner';

import WatcherDashboardPage from './WatcherDashboardPage';
import DeploymentDashboardPage from './DeploymentDashboardPage';
import GovernanceDashboardPage from './GovernanceDashboardPage';
import GovernanceParamsPage from './GovernanceParamsPage';
import MemoryDashboardPage from './MemoryDashboardPage';
import AdminDashboardPage from './AdminDashboardPage';
import VendorManagementTab from './VendorManagementTab';
import ArgusAdminTab from './ArgusAdminTab';
import PlatformFeeLedgerTab from '@/features/admin/PlatformFeeLedgerTab';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard, color: 'text-[#F7931A]' },
  { id: 'argus', label: 'Argus', icon: Eye, color: 'text-teal-400' },
  { id: 'hotels', label: 'Hotels', icon: Hotel, color: 'text-blue-400' },
  { id: 'blockchain', label: 'Blockchain', icon: Activity, color: 'text-emerald-400' },
  { id: 'deployments', label: 'Deployments', icon: Server, color: 'text-purple-400' },
  { id: 'governance', label: 'Governance', icon: Landmark, color: 'text-amber-400' },
  { id: 'parameters', label: 'Parameters', icon: Settings, color: 'text-cyan-400' },
  { id: 'memory', label: 'Memory', icon: Database, color: 'text-pink-400' },
  { id: 'vendors', label: 'Vendors', icon: Package, color: 'text-[#F7931A]' },
  { id: 'platform-fees', label: 'Platform Fees', icon: DollarSign, color: 'text-emerald-400' },
];

function OverviewStatCard({ icon: Icon, label, value, sub, color, testId }) {
  return (
    <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-white/[0.04] border border-white/5 flex items-center justify-center shrink-0">
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xl font-bold text-white heading-font">{value}</p>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
          {sub && <p className="text-[9px] text-gray-600 mt-0.5 truncate">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function SystemHealthRow({ name, status, detail, icon: Icon, color }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-lg bg-white/[0.02] border border-white/5">
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white/[0.04]`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div>
          <p className="text-xs font-medium text-gray-200">{name}</p>
          <p className="text-[10px] text-gray-600">{detail}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className={`w-2 h-2 rounded-full ${status === 'healthy' ? 'bg-emerald-500 animate-pulse' : status === 'warning' ? 'bg-amber-500' : 'bg-red-500'}`} />
        <span className={`text-[10px] font-medium ${status === 'healthy' ? 'text-emerald-400' : status === 'warning' ? 'text-amber-400' : 'text-red-400'}`}>
          {status === 'healthy' ? 'Healthy' : status === 'warning' ? 'Warning' : 'Error'}
        </span>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.allSettled([
        fetch(`${API}/api/admin/stats`).then(r => r.json()),
        fetch(`${API}/api/watcher/status`).then(r => r.json()),
        fetch(`${API}/api/deployments/summary`).then(r => r.json()),
        fetch(`${API}/api/governance/config`).then(r => r.json()),
        fetch(`${API}/api/metaverse/bitsy/memory-dashboard`).then(r => r.json()),
        fetch(`${API}/api/watcher/health`).then(r => r.json()),
      ]);
      const [admin, watcher, deploy, gov, memory, health] = results.map(r => r.status === 'fulfilled' ? r.value : null);
      setData({ admin, watcher, deploy, gov, memory, health });
    } catch {
      toast.error('Failed to load overview');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-[#F7931A]" />
      </div>
    );
  }

  const admin = data?.admin;
  const watcher = data?.watcher;
  const deploy = data?.deploy;
  const gov = data?.gov;
  const memory = data?.memory;
  const health = data?.health;

  const chainsHealthy = health ? Object.values(health).filter(c => c?.healthy).length : 0;
  const chainsTotal = health ? Object.keys(health).length : 0;

  return (
    <div className="space-y-6">
      {/* Title */}
      <div>
        <h1 className="text-2xl sm:text-3xl heading-font font-bold tracking-tight text-white" data-testid="overview-heading">
          Protocol Overview
        </h1>
        <p className="text-sm text-gray-500 mt-1">Real-time health of the HERMES Protocol infrastructure</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="overview-stats">
        <OverviewStatCard icon={Hotel} label="Hotels" value={admin?.total_hotels ?? '—'} color="text-blue-400" testId="stat-hotels" />
        <OverviewStatCard icon={DollarSign} label="Revenue" value={admin ? `$${(admin.total_revenue || 0).toLocaleString()}` : '—'} color="text-emerald-400" testId="stat-revenue" />
        <OverviewStatCard icon={Activity} label="On-Chain Events" value={watcher?.total_events_detected ?? '—'} color="text-[#F7931A]" testId="stat-events" />
        <OverviewStatCard icon={Globe} label="Chains" value={`${chainsHealthy}/${chainsTotal}`} sub="Monitored" color="text-purple-400" testId="stat-chains" />
        <OverviewStatCard icon={Gavel} label="Proposals" value={gov?.proposal_count ?? '—'} color="text-amber-400" testId="stat-proposals" />
        <OverviewStatCard icon={Brain} label="Memories" value={memory?.totals?.total_memories ?? '—'} sub={memory?.totals ? `${memory.totals.compiled_memories || 0} compiled` : ''} color="text-pink-400" testId="stat-memories" />
      </div>

      {/* System Health */}
      <div>
        <h2 className="text-sm font-semibold heading-font text-gray-300 mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-gray-400" />
          System Health
        </h2>
        <div className="space-y-2" data-testid="system-health">
          <SystemHealthRow
            name="Blockchain Watcher"
            icon={Eye}
            color="text-[#F7931A]"
            status={watcher?.running ? 'healthy' : 'error'}
            detail={`${watcher?.chains_monitored?.length || 0} chains, ${watcher?.total_events_detected || 0} events, polling ${watcher?.poll_interval_sec || 15}s`}
          />
          <SystemHealthRow
            name="Contract Deployments"
            icon={Server}
            color="text-purple-400"
            status={deploy?.total_live > 0 ? 'healthy' : 'warning'}
            detail={`${deploy?.total_live || 0} live across ${deploy?.total_chains || 0} chains (${deploy?.testnet_live || 0} testnet, ${deploy?.mainnet_live || 0} mainnet)`}
          />
          <SystemHealthRow
            name="DAO Governance"
            icon={Landmark}
            color="text-amber-400"
            status={gov?.governor ? 'healthy' : 'warning'}
            detail={`${gov?.proposal_count || 0} proposals, threshold ${(gov?.proposal_threshold || 0).toLocaleString()} HERMES`}
          />
          <SystemHealthRow
            name="Memvid Memory"
            icon={Film}
            color="text-pink-400"
            status={memory?.totals?.total_hotels > 0 ? 'healthy' : 'warning'}
            detail={`${memory?.totals?.total_hotels || 0} hotels, ${memory?.totals?.compiled_memories || 0} compiled memories`}
          />
          <SystemHealthRow
            name="Hotel Management"
            icon={Hotel}
            color="text-blue-400"
            status={admin?.total_hotels > 0 ? 'healthy' : 'warning'}
            detail={`${admin?.total_hotels || 0} hotels, ${admin?.total_bookings || 0} bookings, ${admin?.verified_hotels || 0} verified`}
          />
        </div>
      </div>

      {/* Quick Links */}
      <div>
        <h2 className="text-sm font-semibold heading-font text-gray-300 mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-gray-400" />
          Quick Links
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="quick-links">
          {[
            { to: '/protocol', label: 'Protocol Website', icon: Globe, color: 'text-[#F7931A]' },
            { to: '/metaverse', label: '3D World Map', icon: Globe, color: 'text-blue-400' },
            { to: '/vendors', label: 'Vendor Dashboard', icon: BarChart3, color: 'text-emerald-400' },
            { to: '/creator-marketplace', label: 'Creator Marketplace', icon: Users, color: 'text-purple-400' },
            { to: '/faucet', label: 'Testnet Faucet', icon: Zap, color: 'text-amber-400' },
            { to: '/metaverse/economy', label: 'Metaverse Economy', icon: DollarSign, color: 'text-cyan-400' },
            { to: '/platform-admin', label: 'Platform Admin', icon: Shield, color: 'text-pink-400' },
            { to: '/staking', label: 'Reputation Stake', icon: HardDrive, color: 'text-indigo-400' },
          ].map(link => (
            <Link
              key={link.to}
              to={link.to}
              className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.02] border border-white/5 hover:border-white/10 hover:bg-white/[0.04] transition-all group"
              data-testid={`quick-link-${link.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <link.icon className={`w-4 h-4 ${link.color}`} />
              <span className="text-xs text-gray-400 group-hover:text-gray-200 transition-colors">{link.label}</span>
              <ChevronRight className="w-3 h-3 text-gray-700 group-hover:text-gray-500 ml-auto transition-colors" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function GetbitsyAdminPage() {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab />;
      case 'argus': return <ArgusAdminTab />;
      case 'hotels': return <AdminDashboardPage embedded />;
      case 'blockchain': return <WatcherDashboardPage embedded />;
      case 'deployments': return <DeploymentDashboardPage embedded />;
      case 'governance': return <GovernanceDashboardPage embedded />;
      case 'parameters': return <GovernanceParamsPage embedded />;
      case 'memory': return <MemoryDashboardPage embedded />;
      case 'vendors': return <VendorManagementTab />;
      case 'platform-fees': return <PlatformFeeLedgerTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div className="lobby-theme min-h-screen flex" data-testid="getbitsy-admin-page">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 z-50 h-screen w-56 border-r border-white/5 bg-[#060a14] flex flex-col transition-transform duration-200 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-testid="admin-sidebar">
        {/* Brand */}
        <div className="h-14 border-b border-white/5 flex items-center justify-between px-4 shrink-0">
          <Link to="/" className="flex items-center gap-2.5 group">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center shadow-lg shadow-[#F7931A]/10">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold heading-font tracking-tight text-white group-hover:text-[#F7931A] transition-colors">getbitsy</p>
              <p className="text-[8px] text-gray-600 uppercase tracking-widest">Command Center</p>
            </div>
          </Link>
          <button className="lg:hidden text-gray-500 hover:text-gray-300" onClick={() => setSidebarOpen(false)}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map(item => {
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                  active
                    ? 'bg-[#F7931A]/10 border border-[#F7931A]/20 text-white'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/[0.03] border border-transparent'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className={`w-4 h-4 shrink-0 ${active ? 'text-[#F7931A]' : item.color}`} />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-white/5 p-4">
          <div className="flex items-center gap-2 px-1">
            <div className="w-6 h-6 rounded-full bg-[#F7931A]/10 flex items-center justify-center">
              <Shield className="w-3 h-3 text-[#F7931A]" />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-medium">HERMES Protocol</p>
              <p className="text-[8px] text-gray-600">v1.0 — Base Sepolia</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile) */}
        <header className="h-14 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl flex items-center justify-between px-4 shrink-0 lg:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold heading-font text-gray-200">
                {NAV_ITEMS.find(n => n.id === activeTab)?.label || 'Overview'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[9px] border-[#F7931A]/30 text-[#F7931A] bg-[#F7931A]/5">
              Admin
            </Badge>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto">
          <div className={activeTab === 'overview' ? 'max-w-6xl mx-auto px-4 lg:px-6 py-6' : ''}>
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
}
